import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  TextInput,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useListStore, ListInfo } from '../store/listStore';
import { useAuthStore } from '../store/authStore';
import { RootStackParamList } from '../types/navigation';
import * as blueskyService from '../services/bluesky';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export const ListsScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { profile } = useAuthStore();
  const { lists, setLists, addList, removeList, setLoading, isLoading } = useListStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    if (!profile?.did) return;
    
    try {
      setLoading(true);
      const fetchedLists = await blueskyService.getLists(profile.did);
      setLists(fetchedLists);
    } catch (error) {
      console.error('Load lists error:', error);
      Alert.alert('エラー', 'リストの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (name === '') {
      Alert.alert('エラー', 'リスト名を入力してください');
      return;
    }

    try {
      const { uri, cid } = await blueskyService.createList(name, newListDescription.trim());
      
      const newList: ListInfo = {
        uri,
        cid,
        name,
        description: newListDescription.trim(),
        purpose: 'app.bsky.graph.defs#curatelist',
        createdAt: new Date().toISOString(),
      };
      
      addList(newList);
      setNewListName('');
      setNewListDescription('');
      setShowCreateModal(false);
      Alert.alert('成功', 'リストを作成しました');
    } catch (error) {
      console.error('Create list error:', error);
      Alert.alert('エラー', 'リストの作成に失敗しました');
    }
  };

  const handleDeleteList = (list: ListInfo) => {
    Alert.alert(
      'リストを削除',
      `"${list.name}" を削除しますか？この操作は取り消せません。`,
      [
        { text: 'キャンセル', style: 'cancel' },
        {
          text: '削除',
          style: 'destructive',
          onPress: async () => {
            try {
              await blueskyService.deleteList(list.uri);
              removeList(list.uri);
            } catch (error) {
              console.error('Delete list error:', error);
              Alert.alert('エラー', 'リストの削除に失敗しました');
            }
          },
        },
      ]
    );
  };

  const renderListItem = ({ item }: { item: ListInfo }) => (
    <TouchableOpacity
      style={styles.listItem}
      onPress={() => navigation.navigate('ListFeed' as any, { listUri: item.uri, listName: item.name })}
    >
      <View style={styles.listIcon}>
        <Ionicons name="list" size={32} color={colors.primary} />
      </View>
      <View style={styles.listInfo}>
        <Text style={styles.listName}>{item.name}</Text>
        {item.description && (
          <Text style={styles.listDescription} numberOfLines={2}>
            {item.description}
          </Text>
        )}
      </View>
      <TouchableOpacity
        style={styles.deleteButton}
        onPress={() => handleDeleteList(item)}
      >
        <Ionicons name="trash-outline" size={20} color={colors.error} />
      </TouchableOpacity>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>リスト</Text>
        <TouchableOpacity
          style={styles.createButton}
          onPress={() => setShowCreateModal(true)}
        >
          <Ionicons name="add" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : lists.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="list-outline" size={64} color={colors.textSecondary} />
          <Text style={styles.emptyText}>リストがありません</Text>
          <TouchableOpacity
            style={styles.createButtonLarge}
            onPress={() => setShowCreateModal(true)}
          >
            <Text style={styles.createButtonText}>リストを作成</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={lists}
          keyExtractor={(item) => item.uri}
          renderItem={renderListItem}
          contentContainerStyle={styles.listContent}
        />
      )}

      {/* リスト作成モーダル */}
      {showCreateModal && (
        <View style={styles.modal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>新しいリスト</Text>
            
            <TextInput
              style={styles.input}
              placeholder="リスト名"
              placeholderTextColor={colors.textSecondary}
              value={newListName}
              onChangeText={setNewListName}
              maxLength={64}
            />
            
            <TextInput
              style={[styles.input, styles.textArea]}
              placeholder="説明（任意）"
              placeholderTextColor={colors.textSecondary}
              value={newListDescription}
              onChangeText={setNewListDescription}
              multiline
              numberOfLines={3}
              maxLength={300}
            />
            
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setShowCreateModal(false);
                  setNewListName('');
                  setNewListDescription('');
                }}
              >
                <Text style={styles.cancelButtonText}>キャンセル</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={handleCreateList}
              >
                <Text style={styles.confirmButtonText}>作成</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  createButton: {
    padding: spacing.sm,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyText: {
    ...typography.body,
    color: colors.textSecondary,
    marginTop: spacing.md,
    marginBottom: spacing.lg,
  },
  createButtonLarge: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    backgroundColor: colors.primary,
    borderRadius: 24,
  },
  createButtonText: {
    ...typography.body,
    color: colors.background,
    fontWeight: '600',
  },
  listContent: {
    padding: spacing.md,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.card,
    borderRadius: 12,
    marginBottom: spacing.sm,
  },
  listIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.background,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  listInfo: {
    flex: 1,
  },
  listName: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    marginBottom: 4,
  },
  listDescription: {
    ...typography.small,
    color: colors.textSecondary,
  },
  deleteButton: {
    padding: spacing.sm,
  },
  modal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '80%',
    backgroundColor: colors.background,
    borderRadius: 16,
    padding: spacing.lg,
  },
  modalTitle: {
    ...typography.h3,
    color: colors.text,
    marginBottom: spacing.lg,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...typography.body,
    color: colors.text,
    marginBottom: spacing.md,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  modalButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 20,
  },
  cancelButton: {
    backgroundColor: colors.card,
  },
  cancelButtonText: {
    ...typography.body,
    color: colors.text,
  },
  confirmButton: {
    backgroundColor: colors.primary,
  },
  confirmButtonText: {
    ...typography.body,
    color: colors.background,
    fontWeight: '600',
  },
});
